import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Autocomplete,
  IconButton,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Stack,
  Divider,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import { RemoveCircleOutline, Add as AddIcon } from "@mui/icons-material";
import useFetchObjects from "../api/useFetchObjects";
import useAdd from "../api/useAdd";
import { useAuth } from "../context";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";

const getCurrentDate = () => new Date().toISOString().split("T")[0];
const getCurrentTime = () =>
  new Date().toTimeString().split(":").slice(0, 2).join(":");

const PrescriptionDialog = ({
  open,
  onClose,
  patient,
  initialPatientId = null,
  doctor,
  ticketId = null,
}) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const queryClient = useQueryClient();

  const [extraMedicines, setExtraMedicines] = useState([]);
  const [medicines, setMedicines] = useState([]);
  const [autocompleteInputValue, setAutocompleteInputValue] = useState("");
  const autocompleteRef = useRef(null);
  const dosageRefs = useRef([]);
  const quantityRefs = useRef([]);
  const priceRefs = useRef([]);
  const notationRefs = useRef([]);
  const isBeforeRefs = useRef([]);
  const unitsPerMealRefs = useRef([]);
  const timesPer24Refs = useRef([]);

  const { data: medicinesData, isLoading: isLoadingMedicines } =
    useFetchObjects(["stock-medicines"], `stock-medicines/`, token);

  // When opened from ticket context, fetch ticket so prescription is ticket-based (patient comes from ticket)
  const { data: ticketData, isLoading: ticketLoading } = useFetchObjects(
    open && ticketId ? ["ticket-for-prescription", ticketId] : null,
    open && ticketId ? `tickets/${ticketId}/` : "",
    token,
    { enabled: !!(open && ticketId && token) }
  );
  const ticketPatientId =
    ticketData?.patient != null
      ? (typeof ticketData.patient === "object" ? ticketData.patient?.id : ticketData.patient)
      : null;

  useEffect(() => {
    if (medicinesData) {
      // Handle both paginated (results) and non-paginated (array) responses
      const medicinesList = Array.isArray(medicinesData)
        ? medicinesData
        : medicinesData.results || medicinesData.data || [];
      setMedicines(medicinesList);
    } else {
      setMedicines([]);
    }
  }, [medicinesData]);

  const [input, setInput] = useState({
    date: getCurrentDate(),
    time: getCurrentTime(),
    doctor: doctor?.id ?? auth?.user?.user?.id ?? "",
    patient: patient?.id ?? initialPatientId ?? "",
    duration: "",
    total_price: 0,
    items: [],
  });

  useEffect(() => {
    if (open) {
      const patientVal = patient?.id ?? initialPatientId ?? "";
      setInput({
        date: getCurrentDate(),
        time: getCurrentTime(),
        doctor: doctor?.id ?? auth?.user?.user?.id ?? "",
        patient: patientVal !== undefined && patientVal !== null ? patientVal : "",
        duration: "",
        total_price: 0,
        items: [],
      });
      setExtraMedicines([]);
      setFormErrors({});
      setAutocompleteInputValue("");
    }
  }, [open, patient?.id, initialPatientId, doctor?.id, auth?.user?.user?.id]);

  // When opened from ticket and ticket fetch returns, set patient so validation passes
  useEffect(() => {
    if (open && ticketId && ticketPatientId != null) {
      setInput((prev) => ({ ...prev, patient: ticketPatientId }));
    }
  }, [open, ticketId, ticketPatientId]);

  const [formErrors, setFormErrors] = useState({});

  const {
    handleAdd: handleAddPrescription,
    resetForm,
    loading,
  } = useAdd(
    "prescriptions",
    token,
    t("pharmacy.prescriptionCreatedSuccess"),
    t("pharmacy.prescriptionCreateFail"),
  );

  useEffect(() => {
    if (resetForm) {
      const pid = patient?.id ?? initialPatientId;
      if (pid != null) {
        queryClient.invalidateQueries(["prescriptions", pid]);
        queryClient.invalidateQueries(["unified-ehr", pid]);
      }
      setInput({
        date: getCurrentDate(),
        time: getCurrentTime(),
        doctor: doctor?.id ?? auth?.user?.user?.id ?? "",
        patient: patient?.id ?? initialPatientId ?? "",
        duration: "",
        total_price: 0,
        items: [],
      });
      setExtraMedicines([]);
      setFormErrors({});
      setAutocompleteInputValue("");
      onClose();
    }
  }, [resetForm, onClose, queryClient, patient, initialPatientId, doctor, auth]);

  const handleAddNewItem = (medicine) => {
    if (medicine) {
      const medicineName =
        medicine.medicine_display ||
        medicine.name ||
        medicine.medicine?.name ||
        "";
      const availableStock = medicine.quantity || medicine.available_stock || 0;

      setInput((prevInput) => {
        const newItems = [
          ...prevInput.items,
          {
            stock_medicine: medicine.id,
            name: medicineName,
            available_stock: availableStock,
            dosage: "",
            quantity: "",
            sell_price: medicine.sell_price || 0,
            notation: "",
            is_before: "after",
            units_per_meal: 1,
            times_per_24: 1,
            total_price: 0,
          },
        ];
        return {
          ...prevInput,
          items: newItems,
          total_price: calculateTotalPrice(newItems),
        };
      });

      setAutocompleteInputValue("");
      if (autocompleteRef.current) {
        autocompleteRef.current.focus();
      }
    }
  };

  const handleChangeItemField = (index, e) => {
    const { name, value } = e.target;
    const items = [...input.items];
    items[index][name] = value
      ? name === "is_before"
        ? value
        : parseInt(value) || value
      : "";
    if (name === "quantity" || name === "sell_price") {
      const qty = parseInt(items[index].quantity) || 0;
      const price = parseFloat(items[index].sell_price) || 0;
      items[index].total_price = qty * price;
    }
    setInput({ ...input, items, total_price: calculateTotalPrice(items) });
  };

  const handleKeyDown = (e, index, fieldName) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const nextFieldMap = {
        dosage: "units_per_meal",
        units_per_meal: "times_per_24",
        times_per_24: "quantity",
        quantity: "notation",
        notation: null,
      };
      const nextField = nextFieldMap[fieldName];
      if (nextField && nextField !== "notation") {
        const refs = {
          units_per_meal: unitsPerMealRefs,
          times_per_24: timesPer24Refs,
          quantity: quantityRefs,
        };
        if (refs[nextField]?.current[index]) {
          refs[nextField].current[index].focus();
        }
      }
    }
  };

  const handleRemoveItem = (index) => {
    const items = [...input.items];
    items.splice(index, 1);
    setInput({ ...input, items, total_price: calculateTotalPrice(items) });
  };

  const calculateTotalPrice = (items) =>
    items.reduce((sum, item) => sum + (item.total_price || 0), 0);

  const addItems = () => {
    setExtraMedicines([
      ...extraMedicines,
      {
        title: "",
        dosage: "",
        quantity: "",
        notation: "",
        units_per_meal: "",
        times_per_24: "",
        is_before: "",
      },
    ]);
  };

  const removeItem = (index) => {
    setExtraMedicines(extraMedicines?.filter((_, i) => i !== index));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...extraMedicines];
    newItems[index][field] = value;
    setExtraMedicines(newItems);
  };

  const handleSave = (e) => {
    e.preventDefault();
    let isValid = true;
    let errors = {};

    if (!input.date) {
      errors.date = t("pharmacy.dateRequired");
      isValid = false;
    }
    if (!input.doctor) {
      errors.doctor = t("pharmacy.doctorRequired");
      isValid = false;
    }
    // Patient: no frontend validation — send whatever we have; backend validates (or resolves from ticket).

    input.items.forEach((item, index) => {
      if (!item.stock_medicine) {
        errors[`stock_medicine_${index}`] = t("pharmacy.medicineRequired");
        isValid = false;
      }

      if (!item.quantity || parseInt(item.quantity) <= 0) {
        errors[`quantity_${index}`] = t("pharmacy.quantityRequired");
        isValid = false;
      }

      // Dosage validation - can be number or text, but must not be empty
      const dosageStr = item.dosage ? item.dosage.toString().trim() : "";
      if (!dosageStr) {
        errors[`dosage_${index}`] =
          t("pharmacy.dosageRequired") || "Dosage is required";
        isValid = false;
      }
    });

    setFormErrors(errors);

    if (isValid) {
      // Resolve patient: form, prop, initialPatientId (parent), or ticket fetch
      const resolvedPatient =
        input.patient ?? patient?.id ?? initialPatientId ?? ticketPatientId ?? input.patient;
      // Prepare submit data - ensure items don't include name/available_stock (frontend-only fields)
      const submitData = {
        date: input.date,
        time: input.time,
        doctor: input.doctor, // Will be overridden by backend, but send for validation
        patient: resolvedPatient,
        duration: input.duration || null,
        total_price: input.total_price || 0,
        discount: 0,
        ticket: ticketId || null,
        items: input.items.map((item) => {
          // Convert dosage to integer - handle both string and number
          let dosageValue = 0;
          if (item.dosage) {
            const parsed = parseInt(item.dosage.toString().trim());
            if (!isNaN(parsed)) {
              dosageValue = parsed;
            }
          }

          return {
            stock_medicine: item.stock_medicine,
            dosage: dosageValue, // Backend expects integer
            quantity: parseInt(item.quantity) || 0,
            notation: item.notation || null,
            is_before: item.is_before || "after",
            units_per_meal: item.units_per_meal
              ? parseFloat(item.units_per_meal)
              : null,
            times_per_24: item.times_per_24 != null && item.times_per_24 !== ""
              ? parseInt(item.times_per_24, 10)
              : null,
          };
        }),
        extra_medicines: extraMedicines || [],
      };

      handleAddPrescription(submitData);
    }
  };

  // Filter medicines based on autocomplete input value (similar to Doctor module)
  const filteredMedicines = React.useMemo(() => {
    if (!autocompleteInputValue) {
      return medicines;
    }
    const search = autocompleteInputValue.toLowerCase();
    return medicines.filter((medicine) => {
      const medicineDisplay =
        medicine.medicine_display ||
        medicine.name ||
        medicine.medicine?.name ||
        "";
      const categoryDisplay =
        medicine.category_display ||
        medicine.category ||
        medicine.medicine?.category?.name ||
        "";

      return (
        medicineDisplay.toLowerCase().includes(search) ||
        categoryDisplay.toLowerCase().includes(search) ||
        (medicine.name && medicine.name.toLowerCase().includes(search)) ||
        (medicine.medicine?.name &&
          medicine.medicine.name.toLowerCase().includes(search))
      );
    });
  }, [medicines, autocompleteInputValue]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {t("pharmacy.addPrescriptionTitle", "Add Prescription")}
          </Typography>
          {patient && (
            <Typography variant="body2" color="text.secondary">
              {patient.firstname} {patient.lastname}
            </Typography>
          )}
        </Box>
      </DialogTitle>
      <form onSubmit={handleSave}>
        <DialogContent dividers sx={{ maxHeight: "70vh", overflowY: "auto" }}>
          <Grid container spacing={2}>
            {Object.keys(formErrors).length > 0 && (
              <Grid item xs={12}>
                <Alert severity="error">
                  {Object.values(formErrors).map((error, idx) => (
                    <div key={idx}>{error}</div>
                  ))}
                </Alert>
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t("pharmacy.date", "Date")}
                type="date"
                value={input.date}
                onChange={(e) => setInput({ ...input, date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                error={!!formErrors.date}
                helperText={formErrors.date}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t("pharmacy.time", "Time")}
                type="time"
                value={input.time}
                onChange={(e) => setInput({ ...input, time: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t("pharmacy.duration", "Duration (days)")}
                type="number"
                value={input.duration}
                onChange={(e) =>
                  setInput({ ...input, duration: e.target.value })
                }
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>
                {t("pharmacy.medicines", "Medicines")}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                options={filteredMedicines}
                getOptionLabel={(option) =>
                  option.medicine_display ||
                  option.name ||
                  option.medicine?.name ||
                  ""
                }
                value={null}
                inputValue={autocompleteInputValue}
                onInputChange={(event, newInputValue) => {
                  setAutocompleteInputValue(newInputValue);
                }}
                onChange={(event, newValue) => {
                  if (newValue) {
                    handleAddNewItem(newValue);
                    setAutocompleteInputValue("");
                  }
                }}
                loading={isLoadingMedicines}
                noOptionsText={
                  autocompleteInputValue
                    ? t("pharmacy.noMedicinesFound", "No medicines found")
                    : t("pharmacy.typeToSearch", "Type to search for medicines")
                }
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={option.id}>
                    <Typography variant="body1" fontWeight={600}>
                      {option.medicine_display ||
                        option.name ||
                        option.medicine?.name}
                    </Typography>
                    {option.category_display && (
                      <>
                        <Divider
                          orientation="vertical"
                          flexItem
                          sx={{ mx: 1 }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {option.category_display}
                        </Typography>
                      </>
                    )}
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("pharmacy.searchMedicine", "Search Medicine")}
                    placeholder={t(
                      "pharmacy.typeToSearch",
                      "Type to search...",
                    )}
                    inputRef={autocompleteRef}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isLoadingMedicines ? (
                            <CircularProgress size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            {/* Prescription Items Table */}
            {input.items.length > 0 && (
              <Grid item xs={12}>
                <TableContainer component={Paper} elevation={1} sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead sx={{ backgroundColor: "#eef2f5" }}>
                      <TableRow>
                        <TableCell>
                          {t("pharmacy.medicineName", "Medicine Name")}
                        </TableCell>
                        <TableCell align="center">
                          {t("pharmacy.availableStock", "Available Stock")}
                        </TableCell>
                        <TableCell align="center">
                          {t("pharmacy.dosage", "Dosage")}
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: "0.85rem" }}>
                          {t("pharmacy.unitsPerMeal", "Units/Meal")}
                        </TableCell>
                        <TableCell align="center">
                          {t("pharmacy.timesPer24", "Times/24h")}
                        </TableCell>
                        <TableCell align="center">
                          {t("pharmacy.quantity", "Quantity")}
                        </TableCell>
                        <TableCell align="center">
                          {t("pharmacy.price", "Price")}
                        </TableCell>
                        <TableCell align="center">
                          {t("pharmacy.notation", "Notation")}
                        </TableCell>
                        <TableCell align="center">
                          {t("pharmacy.isBeforeMeal", "Is Before Meal")}
                        </TableCell>
                        <TableCell align="center">
                          {t("pharmacy.totalPrice", "Total Price")}
                        </TableCell>
                        <TableCell align="center">
                          {t("pharmacy.action", "Action")}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody sx={{ bgcolor: "#ccfcfc" }}>
                      {input.items.map((item, index) => {
                        const medicine = medicines.find(
                          (m) => m.id === item.stock_medicine,
                        );
                        const medicineName =
                          item.name ||
                          medicine?.name ||
                          medicine?.medicine?.name ||
                          "";
                        const availableStock =
                          item.available_stock !== undefined
                            ? item.available_stock
                            : medicine?.available_stock ||
                              medicine?.quantity ||
                              0;

                        return (
                          <TableRow key={index} hover>
                            <TableCell>{medicineName}</TableCell>
                            <TableCell align="center">
                              {availableStock}
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                inputRef={(el) =>
                                  (dosageRefs.current[index] = el)
                                }
                                name="dosage"
                                type="text"
                                size="small"
                                variant="outlined"
                                value={item.dosage || ""}
                                onChange={(e) =>
                                  handleChangeItemField(index, e)
                                }
                                onKeyDown={(e) =>
                                  handleKeyDown(e, index, "dosage")
                                }
                                sx={{
                                  backgroundColor: "#fff",
                                  borderRadius: 1,
                                  width: "100px",
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                inputRef={(el) =>
                                  (unitsPerMealRefs.current[index] = el)
                                }
                                name="units_per_meal"
                                type="number"
                                size="small"
                                variant="outlined"
                                value={item.units_per_meal || 1}
                                onChange={(e) =>
                                  handleChangeItemField(index, e)
                                }
                                onKeyDown={(e) =>
                                  handleKeyDown(e, index, "units_per_meal")
                                }
                                sx={{
                                  backgroundColor: "#fff",
                                  borderRadius: 1,
                                  width: "80px",
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                inputRef={(el) =>
                                  (timesPer24Refs.current[index] = el)
                                }
                                name="times_per_24"
                                type="number"
                                size="small"
                                variant="outlined"
                                value={item.times_per_24 ?? ""}
                                onChange={(e) =>
                                  handleChangeItemField(index, e)
                                }
                                onKeyDown={(e) =>
                                  handleKeyDown(e, index, "times_per_24")
                                }
                                inputProps={{ min: 1, max: 24 }}
                                placeholder="1"
                                sx={{
                                  backgroundColor: "#fff",
                                  borderRadius: 1,
                                  width: "70px",
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                inputRef={(el) =>
                                  (quantityRefs.current[index] = el)
                                }
                                name="quantity"
                                type="number"
                                size="small"
                                variant="outlined"
                                value={item.quantity || ""}
                                onChange={(e) =>
                                  handleChangeItemField(index, e)
                                }
                                onKeyDown={(e) =>
                                  handleKeyDown(e, index, "quantity")
                                }
                                error={!!formErrors[`quantity_${index}`]}
                                helperText={formErrors[`quantity_${index}`]}
                                sx={{
                                  backgroundColor: "#fff",
                                  borderRadius: 1,
                                  width: "80px",
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                inputRef={(el) =>
                                  (priceRefs.current[index] = el)
                                }
                                name="sell_price"
                                type="number"
                                size="small"
                                variant="outlined"
                                value={item.sell_price || 0}
                                onChange={(e) =>
                                  handleChangeItemField(index, e)
                                }
                                disabled
                                sx={{
                                  backgroundColor: "#f5f5f5",
                                  borderRadius: 1,
                                  width: "100px",
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                inputRef={(el) =>
                                  (notationRefs.current[index] = el)
                                }
                                name="notation"
                                type="text"
                                size="small"
                                variant="outlined"
                                value={item.notation || ""}
                                onChange={(e) =>
                                  handleChangeItemField(index, e)
                                }
                                onKeyDown={(e) =>
                                  handleKeyDown(e, index, "notation")
                                }
                                sx={{
                                  backgroundColor: "#fff",
                                  borderRadius: 1,
                                  width: "120px",
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <FormControl
                                fullWidth
                                size="small"
                                sx={{ minWidth: 100 }}
                              >
                                <Select
                                  value={item.is_before || "after"}
                                  onChange={(e) =>
                                    handleChangeItemField(index, e)
                                  }
                                  name="is_before"
                                  ref={(el) =>
                                    (isBeforeRefs.current[index] = el)
                                  }
                                >
                                  <MenuItem value="before">
                                    {t("pharmacy.before", "Before")}
                                  </MenuItem>
                                  <MenuItem value="med">
                                    {t("pharmacy.med", "Med")}
                                  </MenuItem>
                                  <MenuItem value="after">
                                    {t("pharmacy.after", "After")}
                                  </MenuItem>
                                </Select>
                              </FormControl>
                            </TableCell>
                            <TableCell align="center">
                              <Typography fontWeight={600}>
                                {(item.total_price || 0).toFixed(2)}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip
                                title={t("pharmacy.removeItem", "Remove Item")}
                                arrow
                              >
                                <IconButton
                                  color="error"
                                  size="small"
                                  onClick={() => handleRemoveItem(index)}
                                >
                                  <RemoveCircleOutline />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            )}

            {input.items.length > 0 && (
              <Grid item xs={12}>
                <Box display="flex" justifyContent="flex-end" sx={{ mt: 2 }}>
                  <Typography variant="h6">
                    {t("pharmacy.total", "Total")}:{" "}
                    {input.total_price.toFixed(2)}
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} disabled={loading}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || input.items.length === 0}
            startIcon={loading ? <CircularProgress size={20} /> : <AddIcon />}
          >
            {loading
              ? t("common.saving", "Saving...")
              : t("pharmacy.savePrescription", "Save Prescription")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default PrescriptionDialog;
